import { Trans, useTranslation } from "@onefront/react-sdk";
import { BoxForm, SectionalForm, useWsPatch, useWsPost } from "@onefront/workspace-forms";
import { ISectionalForm } from "@onefront/workspace-forms/typings/interfaces/SectionalForm";
import { VaporTag } from "@vapor/react-custom";
import { Box, Button, Stack, Tooltip } from "@vapor/react-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { InfoBox } from "../../../infobox";
import { BusinessFooterForm } from "../footer";
import { SectionalDto } from "@onefront/workspace-forms/typings/dtos/sectional/SectionalDto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/pro-regular-svg-icons";
import { BusinessDto } from "@onefront/workspace-forms/typings/dtos/business/BusinessDto";

interface BusinessSectionalsFormProps {
    items?: any;
    setItems?: (val: any) => void;
    loading?: boolean;
    onSave?: (val: any) => void;
    onError?: () => void;
    onReset?: () => void;
    onForward?: any;
    onBack?: any;
    isEditing?: any;
    setIsEditing?: (val?: any) => void;
    isNewBusiness?: any;
    businessData?: BusinessDto;
    isCeasedBusiness?: boolean;
}

export const BusinessSectionalsForm = ({
    items,
    loading,
    onSave,
    onError,
    onReset,
    onForward,
    onBack,
    isEditing,
    setIsEditing,
    setItems,
    isCeasedBusiness,
    isNewBusiness,
    businessData
}: BusinessSectionalsFormProps) => {
    const { t } = useTranslation();

    const { workspaceId, businessId } = useParams();

    const [body, setBody] = useState<any>({});
    const [errors, setErrors] = useState<any>({});
    const [displayErrors, setDisplayErrors] = useState<any>(false);

    const sectionalErrors = useMemo(() => {
        return Object.values(errors ?? {}).includes(true);
    }, [errors]);

    const { fetch: fetchPost, loading: loadingPost } = useWsPost(
        `rnd:core:workspace-bff:api://v1/workspace/${workspaceId}/business/sectionals`
    );

    const { fetch: fetchPatch, loading: loadingPatch } = useWsPatch(
        `rnd:core:workspace-bff:api://v1/workspace/${workspaceId}/business/sectionals`,
        {}
    );

    useEffect(() => {
        setErrors({});
        setDisplayErrors(false);
    }, [isEditing]);

    const checkErrors = useCallback(() => {
        if (isEditing === -1) {
            return sectionalErrors;
        } else if (isEditing > -1) {
            let errors = sectionalErrors;

            return errors;
        }
    }, [sectionalErrors, isEditing]);

    const handleSubmit = useCallback(() => {
        if (!checkErrors()) {
            if (isEditing === -1) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { id: tmpId, ...bodyExt } = body;
                fetchPost({
                    data: {
                        ...bodyExt,
                        businessId: businessId
                    }
                })
                    .then((res: any) => {
                        onSave?.({ ...body, ...res.data });
                        setErrors({});
                        setDisplayErrors(false);
                    })
                    .catch(err => {
                        onError?.();
                    });
            } else {
                fetchPatch({
                    url: `workspaceBFF://api/v1/workspace/${workspaceId}/business/sectionals`,
                    data: {
                        businessId: businessId,
                        id: body?.idSectional,
                        description: body?.description,
                        main: body?.main || false
                    }
                })
                    .then((res: any) => {
                        onSave?.({ ...body, ...res.data });
                        setErrors({});
                        setDisplayErrors(false);
                    })
                    .catch(err => {
                        onError?.();
                    });
            }
        } else {
            setDisplayErrors(errors ?? {});
        }
    }, [checkErrors, isEditing, fetchPost, body, onSave, onError, fetchPatch, workspaceId, errors]);

    const existsOtherSectionalTypes = (id: any, type?: any): boolean => {
        let checkExists = false;
        items?.map((it: SectionalDto) => {
            if (it?.type === type && it?.id !== id && it?.main) {
                checkExists = true;
            }
        });

        return checkExists;
    };

    return (
        <>
            <Box sx={{ mb: 3 }}>
                <InfoBox
                    title={t("business.sectionals.info.title")}
                    description={
                        <Trans
                            i18nKey="business.sectionals.info.description"
                            components={{
                                b: <strong />
                            }}
                        />
                    }
                />
            </Box>
            {!isCeasedBusiness && (
                <Box mb={2}>
                    <Button
                        disabled={isEditing || loading || loadingPatch || loadingPost}
                        onClick={() => {
                            setIsEditing?.(-1);
                            setBody({
                                id: -1
                            });
                            setItems?.([
                                {
                                    id: -1
                                },
                                ...items
                            ]);
                            window.scrollTo(0, 0 + 200);
                        }}
                    >
                        <FontAwesomeIcon icon={faCirclePlus} style={{ marginRight: 8 }} /> Aggiungi sezionale
                    </Button>
                </Box>
            )}
            <Stack spacing={2}>
                {workspaceId &&
                    items?.map((it: ISectionalForm) => {
                        if (!it?.id || (!it?.type && it?.id !== -1)) {
                            return null;
                        }
                        const main = it?.id === -1 ? body?.main : it?.main;
                        const type = it?.id === -1 ? body?.type : it?.type;
                        const sectionalData: SectionalDto = {
                            id: it?.id,
                            type: type
                        };

                        return (
                            <BoxForm
                                key={"sectional_boxform_" + it?.id}
                                label={it?.id !== -1 ? it?.name : "Nuovo sezionale"}
                                tag={
                                    <Stack display={"flex"} gap={2} flexDirection={"row"}>
                                        {type && it?.id !== -1 && (
                                            <VaporTag
                                                label={
                                                    type == "PURCHASE"
                                                        ? "SEZIONALE DI ACQUISTO"
                                                        : type == "SALE"
                                                        ? "SEZIONALE DI VENDITA"
                                                        : "REVERSE CHARGE"
                                                }
                                                variant="ghost"
                                                type={
                                                    type == "PURCHASE"
                                                        ? "blueSapphire"
                                                        : type === "SALE"
                                                        ? "prune"
                                                        : "maximumPurple"
                                                }
                                            />
                                        )}
                                        {type &&
                                            main &&
                                            (isEditing !== -1 ||
                                                (isEditing === -1 && it?.id !== -1) ||
                                                (isEditing === -1 &&
                                                    it?.id === -1 &&
                                                    !existsOtherSectionalTypes(-1, type))) && (
                                                <Tooltip
                                                    arrow
                                                    title={
                                                        isEditing === -1
                                                            ? "Sezionale impostato automaticamente come prevalente"
                                                            : ""
                                                    }
                                                >
                                                    <VaporTag
                                                        label="PREVALENTE"
                                                        variant="standard"
                                                        type={type == "PURCHASE" ? "blueSapphire" : "prune"}
                                                    />
                                                </Tooltip>
                                            )}
                                    </Stack>
                                }
                                body={body}
                                loading={(isEditing === it?.id && (loadingPatch || loadingPost)) || loading}
                                editing={isEditing === it?.id}
                                onSubmit={handleSubmit}
                                onReset={() => {
                                    setErrors({});
                                    setDisplayErrors(false);
                                    setIsEditing?.(false);
                                    onReset?.();
                                }}
                                onEdit={
                                    !isEditing && !isCeasedBusiness
                                        ? () => {
                                              setIsEditing?.(it?.id);
                                              setBody(it);
                                          }
                                        : null
                                }
                            >
                                <SectionalForm
                                    body={isEditing === it?.id ? { ...body, businessId: it?.id } : it}
                                    onChange={data =>
                                        setBody({ ...data, main: data?.type === "REVERSE_CHARGE" ? false : data?.main })
                                    }
                                    loading={(isEditing === it?.id && (loadingPatch || loadingPost)) || loading}
                                    type={undefined}
                                    editing={isEditing === it?.id}
                                    workspaceId={workspaceId}
                                    sectionalData={sectionalData}
                                    showPrevalent={
                                        !main || !type || (main && it?.id === -1 && existsOtherSectionalTypes(-1, type))
                                    }
                                    disableReverseCharge={items?.find(
                                        (x: SectionalDto) => x?.type === "REVERSE_CHARGE" && x?.id !== -1
                                    )}
                                    mode={it?.id === -1 ? "create" : "edit"}
                                    businessData={businessData}
                                    onError={errors => {
                                        setErrors(errors);
                                    }}
                                    displayErrors={displayErrors}
                                />
                            </BoxForm>
                        );
                    })}
            </Stack>
            {!isCeasedBusiness && items?.length > 0 && (
                <Box mt={2}>
                    <Button
                        disabled={isEditing || loading || loadingPatch || loadingPost}
                        onClick={() => {
                            setIsEditing?.(-1);
                            setBody({
                                id: -1
                            });
                            setItems?.([
                                {
                                    id: -1
                                },
                                ...items
                            ]);
                            window.scrollTo(0, 0 + 200);
                        }}
                    >
                        <FontAwesomeIcon icon={faCirclePlus} style={{ marginRight: 8 }} /> Aggiungi sezionale
                    </Button>
                </Box>
            )}
            {isNewBusiness && (
                <BusinessFooterForm
                    onForward={onForward}
                    onBack={onBack}
                    loading={false}
                    disabledForward={isEditing || items.filter(item => !item.idSectional)?.length > 0}
                    isNewBusiness={true}
                />
            )}
            <Box height={80} />
        </>
    );
};
